/**
 * Join Requests Management Component
 * Allows group admins/moderators to view and manage pending join requests
 */

import { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Snackbar,
} from '@mui/material'
import {
  Check as CheckIcon,
  Close as CloseIcon,
  HourglassEmpty as PendingIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { socialApi } from '@api/endpoints/social'
import type { GroupJoinRequestDto } from '@api/types/social'
import { JoinRequestStatus } from '@api/types/social'

interface JoinRequestsManagementProps {
  groupId: string
  groupName: string
}

export function JoinRequestsManagement({ groupId, groupName }: JoinRequestsManagementProps) {
  const queryClient = useQueryClient()
  const [selectedRequest, setSelectedRequest] = useState<GroupJoinRequestDto | null>(null)
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null)
  const [reviewNotes, setReviewNotes] = useState('')
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  })

  // Fetch pending join requests
  const {
    data: joinRequests = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['groupJoinRequests', groupId],
    queryFn: () => socialApi.getGroupJoinRequests(groupId),
  })

  // Filter to only show pending requests
  const pendingRequests = joinRequests.filter(req => req.status === JoinRequestStatus.Pending)

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: ({ requestId, notes }: { requestId: string; notes?: string }) =>
      socialApi.approveJoinRequest(requestId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupJoinRequests', groupId] })
      queryClient.invalidateQueries({ queryKey: ['groupMembers', groupId] })
      queryClient.invalidateQueries({ queryKey: ['groupDetails', groupId] })
      setSnackbar({
        open: true,
        message: `${selectedRequest?.displayName || selectedRequest?.userName} has been approved and added to the group`,
        severity: 'success'
      })
      handleCloseDialog()
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to approve request'
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      })
    },
  })

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ requestId, notes }: { requestId: string; notes?: string }) =>
      socialApi.rejectJoinRequest(requestId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupJoinRequests', groupId] })
      setSnackbar({
        open: true,
        message: `${selectedRequest?.displayName || selectedRequest?.userName}'s request has been rejected`,
        severity: 'success'
      })
      handleCloseDialog()
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to reject request'
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      })
    },
  })

  const handleOpenDialog = (request: GroupJoinRequestDto, action: 'approve' | 'reject') => {
    setSelectedRequest(request)
    setActionType(action)
    setReviewNotes('')
  }

  const handleCloseDialog = () => {
    setSelectedRequest(null)
    setActionType(null)
    setReviewNotes('')
  }

  const handleConfirmAction = () => {
    if (!selectedRequest || !actionType) return

    if (actionType === 'approve') {
      approveMutation.mutate({ requestId: selectedRequest.id, notes: reviewNotes })
    } else {
      rejectMutation.mutate({ requestId: selectedRequest.id, notes: reviewNotes })
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load join requests: {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6">Join Requests</Typography>
        {pendingRequests.length > 0 && (
          <Chip
            label={pendingRequests.length}
            color="warning"
            size="small"
          />
        )}
      </Box>

      {pendingRequests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PendingIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            No pending join requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When users request to join {groupName}, they'll appear here
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List sx={{ p: 0 }}>
            {pendingRequests.map((request, index) => (
              <Box key={request.id}>
                <ListItem
                  sx={{
                    py: 2,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={request.userAvatar}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {request.displayName || request.userName}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        {request.message && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontStyle: 'italic',
                              mb: 0.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            "{request.message}"
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Requested {formatDate(request.requestedDate)}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Approve">
                      <IconButton
                        color="success"
                        onClick={() => handleOpenDialog(request, 'approve')}
                        disabled={approveMutation.isPending || rejectMutation.isPending}
                      >
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDialog(request, 'reject')}
                        disabled={approveMutation.isPending || rejectMutation.isPending}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
                {index < pendingRequests.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Paper>
      )}

      {/* Confirm Dialog */}
      <Dialog open={!!selectedRequest && !!actionType} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Join Request' : 'Reject Join Request'}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar src={selectedRequest.userAvatar || undefined} sx={{ width: 48, height: 48 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {selectedRequest.displayName || selectedRequest.userName}
                  </Typography>
                  {selectedRequest.message && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      "{selectedRequest.message}"
                    </Typography>
                  )}
                </Box>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label={actionType === 'approve' ? 'Welcome message (optional)' : 'Reason for rejection (optional)'}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={
                  actionType === 'approve'
                    ? 'Add a welcome message for the new member...'
                    : 'Explain why this request is being rejected...'
                }
              />

              {actionType === 'approve' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {selectedRequest.displayName || selectedRequest.userName} will be added to {groupName} as a Member.
                </Alert>
              )}

              {actionType === 'reject' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {selectedRequest.displayName || selectedRequest.userName} will be notified that their request was rejected.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            onClick={handleConfirmAction}
            disabled={approveMutation.isPending || rejectMutation.isPending}
          >
            {approveMutation.isPending || rejectMutation.isPending
              ? 'Processing...'
              : actionType === 'approve'
              ? 'Approve'
              : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
